import { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { Button } from '@/components/ui/button';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { LogOut } from 'lucide-react';
import { toast } from 'sonner';
import AdminKittens from '@/components/AdminKittens';
import AdminTestimonials from '@/components/AdminTestimonials';
import AdminMessages from '@/components/AdminMessages';
import AdminChat from '@/components/AdminChat';
import AdminParents from '@/components/AdminParents';
import AdminGallery from '@/components/AdminGallery';
import AdminDeposits from '@/components/AdminDeposits';
import AdminVideoCallRequests from '@/components/AdminVideoCallRequests';
import AdminVideos from '@/components/AdminVideos';
import AdminSettings from '@/components/AdminSettings';
import AdminIPManager from '@/components/AdminIPManager';
import VisitorAnalytics from '@/components/VisitorAnalytics';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

export default function AdminDashboard() {
  const navigate = useNavigate();
  const [isAuthenticated, setIsAuthenticated] = useState(null);
  const [kittens, setKittens] = useState([]);
  const [testimonials, setTestimonials] = useState([]);
  const [messages, setMessages] = useState([]);

  // Check authentication on mount
  const checkAuth = useCallback(async () => {
    const token = localStorage.getItem('adminToken');
    if (!token) {
      setIsAuthenticated(false);
      navigate('/admin/login');
      return;
    }

    try {
      const response = await axios.post(
        `${API}/auth/verify`,
        {},
        { headers: { Authorization: `Bearer ${token}` } }
      );

      if (response.data.valid) {
        setIsAuthenticated(true);
      } else {
        localStorage.removeItem('adminToken');
        localStorage.removeItem('adminEmail');
        setIsAuthenticated(false);
        navigate('/admin/login');
      }
    } catch (error) {
      if (process.env.NODE_ENV === 'development') {
        console.error('Auth verification failed:', error);
      }
      localStorage.removeItem('adminToken');
      localStorage.removeItem('adminEmail');
      setIsAuthenticated(false);
      navigate('/admin/login');
    }
  }, [navigate]);

  useEffect(() => {
    checkAuth();
  }, [checkAuth]);

  const handleLogout = async () => {
    const token = localStorage.getItem('adminToken');
    try {
      await axios.post(`${API}/auth/logout`, {}, { 
        headers: token ? { Authorization: `Bearer ${token}` } : {}
      });
    } catch (error) {
      if (process.env.NODE_ENV === 'development') {
        console.error('Logout error:', error);
      }
    }
    localStorage.removeItem('adminToken');
    localStorage.removeItem('adminEmail');
    toast.success('Logged out successfully');
    navigate('/admin/login');
  };

  const fetchKittens = useCallback(async () => {
    try {
      const response = await axios.get(`${API}/kittens`);
      setKittens(response.data);
    } catch {
      toast.error('Failed to load kittens');
    }
  }, []);

  const fetchTestimonials = useCallback(async () => {
    try {
      const response = await axios.get(`${API}/testimonials`);
      setTestimonials(response.data);
    } catch {
      toast.error('Failed to load testimonials');
    }
  }, []);

  const fetchMessages = useCallback(async () => {
    try {
      const response = await axios.get(`${API}/contact`);
      setMessages(response.data);
    } catch {
      toast.error('Failed to load messages');
    }
  }, []);

  useEffect(() => {
    if (isAuthenticated) {
      fetchKittens();
      fetchTestimonials();
      fetchMessages();
    }
  }, [isAuthenticated, fetchKittens, fetchTestimonials, fetchMessages]);

  // Show loading while checking auth
  if (isAuthenticated === null) {
    return (
      <main className="py-20 min-h-screen bg-stone-50 flex items-center justify-center">
        <div className="text-center">
          <div className="w-12 h-12 border-4 border-[#E85D75] border-t-transparent rounded-full animate-spin mx-auto mb-4"></div>
          <p className="text-stone-600">Loading...</p>
        </div>
      </main>
    );
  }

  return (
    <main className="py-20 min-h-screen bg-stone-50">
      <div className="container mx-auto px-4 md:px-8 max-w-7xl">
        <div className="flex items-center justify-between mb-12">
          <h1 className="text-5xl md:text-6xl font-serif font-semibold tracking-tight leading-tight text-stone-800">
            Admin Dashboard
          </h1>
          <Button
            onClick={handleLogout}
            variant="outline"
            className="text-stone-600 hover:text-red-600 hover:border-red-200"
            data-testid="logout-button"
          >
            <LogOut className="w-4 h-4 mr-2" />
            Logout
          </Button>
        </div>

        <Tabs defaultValue="kittens" className="space-y-8">
          {/* Visitor Analytics - Always Visible */}
          <VisitorAnalytics />

          <TabsList className="bg-white border border-stone-200 flex-wrap">
            <TabsTrigger value="kittens" data-testid="kittens-tab">
              Kittens
            </TabsTrigger>
            <TabsTrigger value="videos" data-testid="videos-tab">
              Videos
            </TabsTrigger>
            <TabsTrigger value="deposits" data-testid="deposits-tab">
              Reservations
            </TabsTrigger>
            <TabsTrigger value="videocalls" data-testid="videocalls-tab">
              Video Calls
            </TabsTrigger>
            <TabsTrigger value="gallery" data-testid="gallery-tab">
              Gallery
            </TabsTrigger>
            <TabsTrigger value="parents" data-testid="parents-tab">
              Parents
            </TabsTrigger>
            <TabsTrigger value="testimonials" data-testid="testimonials-tab">
              Testimonials
            </TabsTrigger>
            <TabsTrigger value="chat" data-testid="chat-tab">
              Live Chat
            </TabsTrigger>
            <TabsTrigger value="messages" data-testid="messages-tab">
              Messages
            </TabsTrigger>
            <TabsTrigger value="security" data-testid="security-tab">
              Security
            </TabsTrigger>
            <TabsTrigger value="settings" data-testid="settings-tab">
              Settings
            </TabsTrigger>
          </TabsList>

          {/* Kittens Tab */}
          <TabsContent value="kittens">
            <AdminKittens kittens={kittens} onRefresh={fetchKittens} />
          </TabsContent>

          {/* Videos Tab */}
          <TabsContent value="videos">
            <AdminVideos />
          </TabsContent>

          {/* Gallery Tab */}
          <TabsContent value="gallery">
            <AdminGallery />
          </TabsContent>

          {/* Deposits Tab */}
          <TabsContent value="deposits">
            <AdminDeposits />
          </TabsContent>

          {/* Video Calls Tab */}
          <TabsContent value="videocalls">
            <AdminVideoCallRequests />
          </TabsContent>

          {/* Parents Tab */}
          <TabsContent value="parents">
            <AdminParents />
          </TabsContent>

          {/* Testimonials Tab */}
          <TabsContent value="testimonials">
            <AdminTestimonials testimonials={testimonials} onRefresh={fetchTestimonials} />
          </TabsContent>

          {/* Chat Tab */}
          <TabsContent value="chat">
            <AdminChat />
          </TabsContent>

          {/* Messages Tab */}
          <TabsContent value="messages">
            <AdminMessages messages={messages} onRefresh={fetchMessages} />
          </TabsContent>

          {/* Security Tab */}
          <TabsContent value="security">
            <AdminIPManager />
          </TabsContent>

          {/* Settings Tab */}
          <TabsContent value="settings">
            <AdminSettings />
          </TabsContent>
        </Tabs>
      </div>
    </main>
  );
}
